import {
  requestDownloadBundle,
  requestUpdateBundle,
} from '@/api/vocabulary-management';
import {
  ErrorReason,
  VocabBundleWithState,
  VocabularyBundleStatus,
} from '@/types/vocabulary-management/VocabularyBundle';
import {
  Box,
  Button,
  Chip,
  ContentBlock,
  Grid,
  Icon,
  RowProps,
  Spinner,
  Tooltip,
  Typography,
  useNotifications,
} from '@prometheus/components';
import { Nullable, Status } from '@prometheus/types';
import { DateFormat, formatDate } from '@prometheus/utils';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { VocabularyList } from '../VocabularyList/VocabularyList';
import { IconWrapper } from './BundleTile.styles';
import { errorReasonMap } from '@/utils/errorReasonMap';

export interface BundleTileProps {
  className: string;
  value: VocabBundleWithState;
  row: RowProps<VocabBundleWithState>;
  onClick: (row: RowProps<VocabBundleWithState>) => Promise<void>;
  onReload: () => Promise<void>;
}
export const BundleTile: FC<BundleTileProps> = ({
  className,
  value,
  row,
  onReload,
}) => {
  const [downloadStatus, setDownloadStatus] = useState(Status.INITIAL);
  const { t } = useTranslation();
  const { enqueueSnackbar } = useNotifications();

  const onDownloadClick = async row => {
    setDownloadStatus(Status.IN_PROGRESS);
    const externalId = row.original.externalId;
    const body = row.original;
    return requestDownloadBundle(externalId, body)
      .then(async () => {
        enqueueSnackbar({
          message: t('notifications.download_success'),
          variant: 'success',
        });
        await onReload();
        setDownloadStatus(Status.SUCCESS);
      })
      .catch(err => {
        enqueueSnackbar({
          message: t('notifications.download_already_sent'),
          variant: 'warning',
        });
        setDownloadStatus(Status.ERROR);
      });
  };

  const onUpdateClick = async row => {
    setDownloadStatus(Status.IN_PROGRESS);
    const externalId = row.original.externalId;
    const body = row.original;
    return requestUpdateBundle(externalId, body)
      .then(async () => {
        enqueueSnackbar({
          message: t('notifications.update_success'),
          variant: 'success',
        });
        await onReload();
        setDownloadStatus(Status.SUCCESS);
      })
      .catch(err => {
        console.error(err);
        setDownloadStatus(Status.ERROR);
      });
  };

  return (
    <Grid
      item
      xs={12}
      className={className}
      sx={{
        '.collapsible-content-block__title': {
          height: 60,
        },
      }}
    >
      <ContentBlock
        collapsible
        unstyled
        defaultState={false}
        title={
          <Grid container pl={1}>
            <Grid item xs={12} lineHeight={'30px'}>
              <Typography variant="h2" display="inline-block">
                {value.name}
              </Typography>
              {value.releaseVersion && (
                <Chip
                  size="small"
                  label={value.releaseVersion}
                  sx={{
                    borderRadius: 0.5,
                    color: 'textColor.header',
                    fontSize: 12,
                    ml: 1,
                    height: 22,
                  }}
                />
              )}
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="textColor.label">
                {formatDate(value.lastUpdated, DateFormat.DATE_TIME, true)}
              </Typography>
            </Grid>
          </Grid>
        }
        actions={
          value.externalId ? (
            <Box display="flex" flexWrap="nowrap" alignItems="center">
              <Button
                color="info"
                variant="contained"
                disabled={value.inProgress || !value.downloadable}
                startIcon={<Icon iconName="download" sx={{ fontSize: 16 }} />}
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  onDownloadClick(row);
                }}
                sx={{ mr: 1 }}
              >
                {t('buttons.download')}
              </Button>
              <Tooltip
                text={
                  value.updatable
                    ? value?.newVersionInfo?.releaseVersion
                      ? 'New version: ' + value?.newVersionInfo?.releaseVersion
                      : undefined
                    : undefined
                }
              >
                <Button
                  color="info"
                  variant="outlined"
                  disabled={
                    value.inProgress || !value.updatable || value.downloadable
                  }
                  startIcon={<Icon iconName="reload" sx={{ fontSize: 16 }} />}
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    onUpdateClick(row);
                  }}
                  sx={{ mr: 1 }}
                >
                  {t('buttons.update')}
                </Button>
              </Tooltip>
              {renderStatus(value.downloadStatus, value.errorReason)}
            </Box>
          ) : (
            <></>
          )
        }
      >
        <VocabularyList data={value.vocabularies} />
      </ContentBlock>
    </Grid>
  );
};

function renderStatus(
  status: Nullable<VocabularyBundleStatus>,
  errorReason: ErrorReason
) {
  let severity = '';
  let component = <></>;
  let message = '';

  switch (status) {
    case VocabularyBundleStatus.REQUEST_EXISTS:
    case VocabularyBundleStatus.GENERATION:
    case VocabularyBundleStatus.DOWNLOADING:
    case VocabularyBundleStatus.REQUEST_NEW:
      component = <Spinner size={20} />;
      severity = 'info';
      message = 'Downloading';
      break;
    case VocabularyBundleStatus.FAILED:
    case VocabularyBundleStatus.FAILED_NEW:
      component = <Icon iconName="warning" color="error" />;
      severity = 'error';
      message = errorReasonMap[errorReason];
      break;
    case VocabularyBundleStatus.DOWNLOADED:
      component = <Icon iconName="downloadDone" color="success" />;
      severity = 'success';
      message = 'Downloaded';
      break;
  }
  return (
    <Tooltip text={message}>
      <IconWrapper severity={severity}>{component}</IconWrapper>
    </Tooltip>
  );
}
